import React, { useRef } from 'react';
import { flexRender, type ReactTable, type Row } from '@tanstack/react-table';

import { useTranslation } from 'src/i18n';
import { cn } from 'src/component/ds/utils';
import { Skeleton } from 'src/component/ds/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/component/ds/ui/table';
import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';

import { hidesBottomBorder, isBlankCell } from 'src/component/ds/dataTable/layout';
import { useLoadMoreOnScroll } from 'src/component/ds/dataTable/useLoadMoreOnScroll';
import type { DsAnyRow } from 'src/component/ds/dataTable/types';

export interface DsDataTableProps<TData extends DsAnyRow> {
  /** From `useDsTable`. */
  table: ReactTable<DataTableFeatures, TData>;

  /** Row background etc. Cell and column classes paint over it. */
  rowClassName?: (row: Row<DataTableFeatures, TData>) => string | undefined;
  onRowClick?: (row: Row<DataTableFeatures, TData>) => void;

  /** Lazy load: `onLoadMore` fires when the bottom of the list scrolls into view. */
  hasMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;

  emptyMessage?: React.ReactNode;
  /** Sizes the scroll area, e.g. 'ds-h-[600px]'. Without a height it never scrolls. */
  containerClassName?: string;
  /** Sizes the bordered box instead, e.g. 'ds-flex-1 ds-min-h-0' to fill a flex column. */
  className?: string;
}

const LOADING_ROWS = 3;

/* `hidden` beats the row's border in the collapsed-border model. Unprefixed:
 * Tailwind doesn't apply `ds-` to arbitrary properties. */
const HIDE_BOTTOM_BORDER = '[border-bottom-style:hidden]';

/* A collapsed <tr> border stays put while the header sticks, so the header
 * draws its own bottom line. */
const STICKY_HEAD = 'ds-sticky ds-top-0 ds-z-[1] ds-shadow-[inset_0_-1px_0_var(--ds-border)]';

/* Keeps a click inside a cell's buttons/dropdowns from also clicking the row.
 * React bubbles through portals, so this covers Radix menu items too. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();


const isRowActivation = (e: React.KeyboardEvent) =>
  e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ');

/*
 * Renders a `useDsTable` table: child rows under their parents, repeated group
 * values hidden, parent columns without inner borders, sticky header, and more
 * rows on scroll. Columns are plain TanStack column defs; see the README.
 */
export function DsDataTable<TData extends DsAnyRow> ({
  table,
  rowClassName,
  onRowClick,
  hasMore,
  loading,
  onLoadMore,
  emptyMessage,
  containerClassName,
  className,
}: DsDataTableProps<TData>) {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLTableRowElement>(null);

  // flatRows lists each row followed by its children -- render order. That holds
  // because useDsTable keeps sorting/filtering manual; client-side sorting would
  // leave flatRows in data order.
  const rows = table.getRowModel().flatRows;
  const columns = table.getVisibleLeafColumns();
  const groupingColumns = columns.filter((c) => c.columnDef.meta?.hideRepeats);

  useLoadMoreOnScroll(sentinelRef, !!hasMore && !loading, onLoadMore);

  return (
    <div className={cn('ds-flex ds-w-full ds-flex-col ds-overflow-hidden ds-rounded-md ds-border ds-border-border ds-bg-background', className)}>
      {/* Table's wrapper div scrolls, so the sticky header sticks to it. Relative
        * so absolute children (sr-only labels) clip here instead of growing the page. */}
      <Table containerClassName={cn('ds-relative ds-min-h-0 ds-flex-1 ds-overflow-y-auto', containerClassName)}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                return (
                  <TableHead
                    key={header.id}
                    className={cn(STICKY_HEAD, 'ds-whitespace-nowrap', meta?.className, meta?.headerClassName)}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const rowBelow = rows[rowIndex + 1];
            // The checkbox has no group of its own; it follows the rest of its row.
            const rowHidesBorder = columns.some((c) => hidesBottomBorder(c, row, rowBelow, groupingColumns));
            return (
              <TableRow
                key={row.id}
                interactive={!!onRowClick}
                data-depth={row.depth}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                className={cn(row.subRows.length > 0 && 'ds-font-medium', rowClassName?.(row))}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (!isRowActivation(e)) return;
                  e.preventDefault(); // Space would scroll the table
                  onRowClick(row);
                } : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const { column } = cell;
                  const meta = column.columnDef.meta;
                  const blank = isBlankCell(column, row, groupingColumns);
                  const hideBorder = column.id === 'select'
                    ? rowHidesBorder
                    : hidesBottomBorder(column, row, rowBelow, groupingColumns);
                  return (
                    <TableCell
                      key={cell.id}
                      data-continues={hideBorder || undefined}
                      className={cn(
                        'ds-py-2',
                        hideBorder && HIDE_BOTTOM_BORDER,
                        meta?.className,
                        !blank && meta?.cellClassName?.(cell)
                      )}
                      onClick={meta?.interactive ? stop : undefined}
                    >
                      {blank ? null : flexRender(column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}

          {!rows.length && !loading && !hasMore && (
            <TableRow>
              <TableCell colSpan={columns.length} className='ds-h-24 ds-text-center ds-text-muted-foreground'>
                {emptyMessage ?? t('table.noRecords')}
              </TableCell>
            </TableRow>
          )}

          {loading && Array.from({ length: LOADING_ROWS }, (_, i) => (
            <TableRow key={`loading-${i}`} data-testid='ds-table-loading-row' aria-hidden='true'>
              {columns.map((column) => (
                <TableCell key={column.id} className={cn('ds-py-3', column.columnDef.meta?.className)}>
                  <Skeleton className={column.id === 'select' ? 'ds-size-4' : 'ds-h-4 ds-w-full'} />
                </TableCell>
              ))}
            </TableRow>
          ))}

          {hasMore && (
            <tr ref={sentinelRef} aria-hidden='true' data-testid='ds-table-sentinel'>
              <td colSpan={columns.length} className='ds-h-px ds-p-0' />
            </tr>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
